import { useEffect, useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { db } from '@/lib/firebase';
import { collection, query, getDocs, doc, setDoc, deleteDoc, Timestamp, onSnapshot, addDoc } from 'firebase/firestore';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Search, Heart, MessageSquare, MapPin, Star, Calendar, Users, Briefcase } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const contactSchema = z.object({
  subject: z.string().min(5, 'Subject must be at least 5 characters').max(100),
  message: z.string().min(20, 'Message must be at least 20 characters').max(1000),
  preferredDate: z.string().optional(),
  preferredTime: z.string().optional(),
});

type ContactFormData = z.infer<typeof contactSchema>;

interface Consultant {
  id: string;
  name: string;
  expertise: string;
  location?: string;
  languages?: string[];
  bio?: string;
  rate?: string;
  hourlyRate?: number;
  avatarUrl?: string;
  rating?: number;
  totalReviews?: number;
  yearsExperience?: number;
  specializations?: string[];
  availability?: 'available' | 'busy' | 'unavailable';
}

const fallbackConsultants: Consultant[] = [
  {
    id: 'c1',
    name: 'Sophie Dubois',
    expertise: 'Business Valuation',
    location: 'Quebec City, QC',
    bio: 'Expert in SME valuations and due diligence with 15 years of experience',
    rate: '$150/hour',
    hourlyRate: 150,
    rating: 4.8,
    totalReviews: 127,
    yearsExperience: 15,
    languages: ['English', 'French'],
    specializations: ['Business Valuation', 'Due Diligence', 'Financial Analysis'],
    availability: 'available',
  },
  {
    id: 'c2',
    name: 'Marc Tremblay',
    expertise: 'Corporate Law',
    location: 'Montreal, QC',
    bio: 'Corporate law specialist focusing on M&A transactions and business law',
    rate: '$200/hour',
    hourlyRate: 200,
    rating: 4.9,
    totalReviews: 203,
    yearsExperience: 20,
    languages: ['English', 'French'],
    specializations: ['M&A Law', 'Corporate Governance', 'Contract Negotiation'],
    availability: 'available',
  },
  {
    id: 'c3',
    name: 'Nathalie Vachon',
    expertise: 'Tax Advisor',
    location: 'Quebec City, QC',
    bio: 'Tax optimization specialist for SME acquisitions and business transitions',
    rate: '$180/hour',
    hourlyRate: 180,
    rating: 4.7,
    totalReviews: 89,
    yearsExperience: 12,
    languages: ['English', 'French'],
    specializations: ['Tax Planning', 'Business Acquisitions', 'Tax Compliance'],
    availability: 'busy',
  },
];

const LenderConsultants = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [consultants, setConsultants] = useState<Consultant[]>([]);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterExpertise, setFilterExpertise] = useState<string>('all');
  const [filterAvailability, setFilterAvailability] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('rating');
  const [selectedConsultant, setSelectedConsultant] = useState<Consultant | null>(null);
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);

  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      subject: '',
      message: '',
      preferredDate: '',
      preferredTime: '',
    },
  });

  useEffect(() => {
    fetchConsultants();
  }, []);

  useEffect(() => {
    if (!user) return;
    
    const favoritesRef = collection(db, 'users', user.id, 'consultantsFavorites');
    const unsubscribe = onSnapshot(favoritesRef, (snapshot) => {
      const favSet = new Set<string>();
      snapshot.forEach((doc) => {
        favSet.add(doc.id);
      });
      setFavorites(favSet);
    });

    return () => unsubscribe();
  }, [user]);

  const fetchConsultants = async () => {
    try {
      setLoading(true);
      const consultantsQuery = query(collection(db, 'consultants'));
      const consultantsSnapshot = await getDocs(consultantsQuery);

      if (consultantsSnapshot.empty) {
        setConsultants(fallbackConsultants);
      } else {
        const consultantsList = consultantsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })) as Consultant[];
        setConsultants(consultantsList);
      }
    } catch (error) {
      console.error('Error fetching consultants:', error);
      setConsultants(fallbackConsultants);
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorite = async (consultantId: string) => {
    if (!user) {
      toast({
        title: 'Authentication Required',
        description: 'Please sign in to save favorites',
        variant: 'destructive',
      });
      return;
    }

    try {
      const favoriteRef = doc(db, 'users', user.id, 'consultantsFavorites', consultantId);
      
      if (favorites.has(consultantId)) {
        await deleteDoc(favoriteRef);
        toast({ title: 'Removed from favorites' });
      } else {
        await setDoc(favoriteRef, { addedAt: Timestamp.now() });
        toast({ title: 'Added to favorites' });
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast({
        title: 'Error',
        description: 'Failed to update favorites',
        variant: 'destructive',
      });
    }
  };

  const handleOpenContactDialog = (consultant: Consultant) => {
    setSelectedConsultant(consultant);
    form.reset({
      subject: `Consultation Request - ${consultant.expertise}`,
      message: '',
      preferredDate: '',
      preferredTime: '',
    });
    setIsContactDialogOpen(true);
  };

  const onSubmitContact = async (data: ContactFormData) => {
    if (!user || !selectedConsultant) return;

    try {
      await addDoc(collection(db, 'contactRequests'), {
        type: 'consultant',
        consultantId: selectedConsultant.id,
        consultantName: selectedConsultant.name,
        fromUserId: user.id,
        fromUserEmail: user.email,
        subject: data.subject,
        message: data.message,
        preferredDate: data.preferredDate || null,
        preferredTime: data.preferredTime || null,
        status: 'pending',
        createdAt: Timestamp.now(),
      });

      toast({ title: 'Contact request sent successfully' });
      form.reset();
      setIsContactDialogOpen(false);
      setSelectedConsultant(null);
    } catch (error) {
      console.error('Error sending contact request:', error);
      toast({
        title: 'Error',
        description: 'Failed to send contact request',
        variant: 'destructive',
      });
    }
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getAvailabilityColor = (availability?: string) => {
    const colors: Record<string, string> = {
      available: 'bg-green-500',
      busy: 'bg-yellow-500',
      unavailable: 'bg-red-500',
    };
    return colors[availability || 'available'] || 'bg-gray-500';
  };

  const getAvailabilityLabel = (availability?: string) => {
    const labels: Record<string, string> = {
      available: 'Available',
      busy: 'Limited Availability',
      unavailable: 'Unavailable',
    };
    return labels[availability || 'available'] || 'Unknown';
  };

  const filteredAndSortedConsultants = useMemo(() => {
    let filtered = consultants.filter(consultant => {
      const matchesSearch = consultant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           consultant.expertise.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           consultant.bio?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           consultant.location?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesExpertise = filterExpertise === 'all' || consultant.expertise === filterExpertise;
      const matchesAvailability = filterAvailability === 'all' || consultant.availability === filterAvailability;
      
      return matchesSearch && matchesExpertise && matchesAvailability;
    });

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'rating':
          return (b.rating || 0) - (a.rating || 0);
        case 'experience':
          return (b.yearsExperience || 0) - (a.yearsExperience || 0);
        case 'rate':
          return (a.hourlyRate || 0) - (b.hourlyRate || 0);
        case 'name':
          return a.name.localeCompare(b.name);
        default:
          return 0;
      }
    });

    return filtered;
  }, [consultants, searchTerm, filterExpertise, filterAvailability, sortBy]);

  const expertiseOptions = Array.from(new Set(consultants.map(c => c.expertise)));

  const stats = {
    total: consultants.length,
    favorites: favorites.size,
    available: consultants.filter(c => c.availability === 'available').length,
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">My Consultants</h1>
          <p className="text-muted-foreground mt-1">
            Find and connect with business consultants and experts
          </p>
        </div>
        <Users className="h-8 w-8 text-primary" />
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Consultants</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">My Favorites</CardTitle>
            <Heart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.favorites}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Available Now</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.available}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <CardTitle>Find Consultants</CardTitle>
          <CardDescription>Search and filter by expertise, availability, and more</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search consultants..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <Select value={filterExpertise} onValueChange={setFilterExpertise}>
              <SelectTrigger>
                <SelectValue placeholder="All Expertise" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Expertise</SelectItem>
                {expertiseOptions.map(expertise => (
                  <SelectItem key={expertise} value={expertise}>{expertise}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterAvailability} onValueChange={setFilterAvailability}>
              <SelectTrigger>
                <SelectValue placeholder="All Availability" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Availability</SelectItem>
                <SelectItem value="available">Available</SelectItem>
                <SelectItem value="busy">Limited Availability</SelectItem>
                <SelectItem value="unavailable">Unavailable</SelectItem>
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger>
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="rating">Highest Rated</SelectItem>
                <SelectItem value="experience">Most Experience</SelectItem>
                <SelectItem value="rate">Lowest Rate</SelectItem>
                <SelectItem value="name">Name (A-Z)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredAndSortedConsultants.length > 0 ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredAndSortedConsultants.map((consultant) => (
                <Card key={consultant.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-start gap-3">
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={consultant.avatarUrl} alt={consultant.name} />
                        <AvatarFallback>{getInitials(consultant.name)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-lg">{consultant.name}</CardTitle>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleFavorite(consultant.id)}
                            className="flex-shrink-0"
                          >
                            <Heart
                              className={`h-4 w-4 ${
                                favorites.has(consultant.id)
                                  ? 'fill-red-500 text-red-500'
                                  : 'text-muted-foreground'
                              }`}
                            />
                          </Button>
                        </div>
                        <CardDescription className="flex items-center gap-1 mt-1">
                          <Briefcase className="h-3 w-3" />
                          {consultant.expertise}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-1">
                        <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                        <span className="font-semibold">{consultant.rating?.toFixed(1) || 'N/A'}</span>
                        <span className="text-muted-foreground">({consultant.totalReviews || 0} reviews)</span>
                      </div>
                      <Badge variant="outline" className="flex items-center gap-1">
                        <span className={`w-2 h-2 rounded-full ${getAvailabilityColor(consultant.availability)}`} />
                        {getAvailabilityLabel(consultant.availability)}
                      </Badge>
                    </div>

                    {consultant.location && (
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <MapPin className="h-3 w-3" />
                        {consultant.location}
                      </div>
                    )}

                    <p className="text-sm text-muted-foreground line-clamp-2">{consultant.bio}</p>

                    {consultant.specializations && consultant.specializations.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {consultant.specializations.slice(0, 3).map((spec, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs">
                            {spec}
                          </Badge>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-2">
                      <span className="text-sm font-medium text-primary">{consultant.rate}</span>
                      <Button size="sm" onClick={() => handleOpenContactDialog(consultant)}>
                        <MessageSquare className="h-4 w-4 mr-1" />
                        Contact
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold">No consultants found</h3>
              <p className="text-muted-foreground">Try adjusting your search or filters</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Contact Dialog */}
      <Dialog open={isContactDialogOpen} onOpenChange={setIsContactDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Contact {selectedConsultant?.name}</DialogTitle>
            <DialogDescription>
              Send a message to request a consultation
            </DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmitContact)} className="space-y-4">
              <FormField
                control={form.control}
                name="subject"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Subject</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="message"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Message</FormLabel>
                    <FormControl>
                      <Textarea rows={4} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="preferredDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Preferred Date (optional)</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="preferredTime"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Preferred Time (optional)</FormLabel>
                      <FormControl>
                        <Input type="time" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsContactDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">Send Request</Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LenderConsultants;
